/**
 * Panel para pasar lista de asistencia
 */

import { useState, useMemo, useEffect } from "react";
import { Button } from "@/components/ui";
import { Can } from "@/components/auth/Can";
import { useConfirm } from "@/components/ui/ConfirmDialog";
import {
  X,
  Loader2,
  Search,
  Check,
  CheckCircle,
  XCircle,
  Clock,
  Users,
  Save,
  AlertCircle,
  UserCheck,
} from "lucide-react";
import {
  useAttendanceByMeeting,
  useMeetingStats,
  useCreateBulkAttendance,
  useUpdateBulkAttendance,
  useCloseMeeting,
  useReopenMeeting,
} from "@/hooks/useAttendance";
import { useMembersByClub } from "@/hooks/usePersons";
import { toJsDate } from "@/lib/services/activitySubmissions";
import type {
  Meeting,
  AttendanceStatus,
  MemberLegacy as Member,
} from "@/types";

interface AttendancePanelProps {
  isOpen: boolean;
  onClose: () => void;
  meeting: Meeting;
}

const statusButtons: Array<{
  value: AttendanceStatus;
  label: string;
  icon: React.ReactNode;
  color: string;
}> = [
  {
    value: "present",
    label: "Presente",
    icon: <Check className="w-4 h-4" />,
    color: "bg-green-100 text-green-700 hover:bg-green-200",
  },
  {
    value: "absent",
    label: "Ausente",
    icon: <XCircle className="w-4 h-4" />,
    color: "bg-red-100 text-red-700 hover:bg-red-200",
  },
  {
    value: "late",
    label: "Tardanza",
    icon: <Clock className="w-4 h-4" />,
    color: "bg-yellow-100 text-yellow-700 hover:bg-yellow-200",
  },
  {
    value: "excused",
    label: "Justificado",
    icon: <AlertCircle className="w-4 h-4" />,
    color: "bg-blue-100 text-blue-700 hover:bg-blue-200",
  },
];

export default function AttendancePanel({
  isOpen,
  onClose,
  meeting,
}: AttendancePanelProps) {
  const [searchTerm, setSearchTerm] = useState("");
  const [localAttendance, setLocalAttendance] = useState<
    Map<string, { status: AttendanceStatus; justification?: string }>
  >(new Map());
  const [hasChanges, setHasChanges] = useState(false);

  const { data: existingAttendance, isLoading: loadingAttendance } =
    useAttendanceByMeeting(meeting.id);
  const { data: stats, refetch: refetchStats } = useMeetingStats(meeting.id);
  const { data: members, isLoading: loadingMembers } = useMembersByClub(
    meeting.clubId,
  );
  const createBulkMutation = useCreateBulkAttendance();
  const updateBulkMutation = useUpdateBulkAttendance();
  const closeMutation = useCloseMeeting();
  const reopenMutation = useReopenMeeting();
  const confirm = useConfirm();

  // Inicializar asistencia cuando se cargan los datos
  useEffect(() => {
    if (existingAttendance && existingAttendance.length > 0) {
      const attendanceMap = new Map<
        string,
        { status: AttendanceStatus; justification?: string }
      >();
      existingAttendance.forEach((a) => {
        attendanceMap.set(a.id, {
          status: a.status,
          justification: a.justification,
        });
      });
      setLocalAttendance(attendanceMap);
    }
  }, [existingAttendance]);

  // Verificar si necesitamos crear registros de asistencia
  const needsInitialization =
    !loadingAttendance &&
    (!existingAttendance || existingAttendance.length === 0);

  // Inicializar asistencia para todos los miembros
  const handleInitializeAttendance = async () => {
    if (!members || members.length === 0) return;

    const memberData = members
      .filter((m: Member) => m.status === "active")
      .map((m: Member) => ({
        memberId: m.id,
        personId: m.personId || m.id, // Usar memberId como fallback
        personName: m.fullName || `${m.firstName} ${m.lastName}`,
      }));

    await createBulkMutation.mutateAsync({
      meetingId: meeting.id,
      clubId: meeting.clubId,
      date: meeting.date,
      members: memberData,
      defaultStatus: "absent",
    });
  };

  // Cambiar estado de asistencia local
  const handleStatusChange = (
    attendanceId: string,
    status: AttendanceStatus,
  ) => {
    setLocalAttendance((prev) => {
      const newMap = new Map(prev);
      const current = newMap.get(attendanceId);
      newMap.set(attendanceId, { ...current, status });
      return newMap;
    });
    setHasChanges(true);
  };

  // Guardar todos los cambios
  const handleSave = async () => {
    if (!existingAttendance) return;

    const updates = existingAttendance
      .filter((a) => {
        const local = localAttendance.get(a.id);
        return (
          local &&
          (local.status !== a.status || local.justification !== a.justification)
        );
      })
      .map((a) => {
        const local = localAttendance.get(a.id)!;
        return {
          id: a.id,
          status: local.status,
          justification: local.justification,
        };
      });

    if (updates.length > 0) {
      await updateBulkMutation.mutateAsync({
        updates,
        meetingId: meeting.id,
      });
      setHasChanges(false);
      refetchStats();
    }
  };

  // Cerrar reunión: guarda lo pendiente, registra estadísticas finales y la marca
  // como cerrada (reabrible). attendanceCount = presentes + tardanzas.
  const handleCloseMeeting = async () => {
    if (!existingAttendance) return;
    const ok = await confirm({
      title: "Cerrar reunión",
      description:
        "Se guardará la asistencia actual y la reunión quedará cerrada. Podrás reabrirla si necesitas hacer ajustes.",
      variant: "info",
      confirmText: "Cerrar reunión",
    });
    if (!ok) return;

    const pending = existingAttendance
      .filter((a) => {
        const local = localAttendance.get(a.id);
        return (
          local &&
          (local.status !== a.status || local.justification !== a.justification)
        );
      })
      .map((a) => {
        const local = localAttendance.get(a.id)!;
        return {
          id: a.id,
          status: local.status,
          justification: local.justification,
        };
      });
    if (pending.length > 0) {
      await updateBulkMutation.mutateAsync({
        updates: pending,
        meetingId: meeting.id,
      });
    }

    const finalStatusOf = (a: (typeof existingAttendance)[number]) =>
      localAttendance.get(a.id)?.status ?? a.status;
    const presentCount = existingAttendance.filter((a) => {
      const s = finalStatusOf(a);
      return s === "present" || s === "late";
    }).length;

    await closeMutation.mutateAsync({
      id: meeting.id,
      attendanceCount: presentCount,
      totalMembers: existingAttendance.length,
      clubId: meeting.clubId,
    });
    setHasChanges(false);
    onClose();
  };

  const handleReopenMeeting = async () => {
    await reopenMutation.mutateAsync({
      id: meeting.id,
      clubId: meeting.clubId,
    });
    onClose();
  };

  // Marcar todos como presente
  const handleMarkAllPresent = async () => {
    if (!existingAttendance) return;

    const ok = await confirm({
      title: "Marcar todos como presentes",
      description: `Se marcarán ${existingAttendance.length} miembros como presentes. Aún no se guardará hasta que presiones "Guardar".`,
      variant: "info",
      confirmText: "Marcar todos",
    });
    if (!ok) return;

    const newMap = new Map<
      string,
      { status: AttendanceStatus; justification?: string }
    >();
    existingAttendance.forEach((a) => {
      newMap.set(a.id, { status: "present" });
    });
    setLocalAttendance(newMap);
    setHasChanges(true);
  };

  // Cambiar justificación local (para "Justificado")
  const handleJustificationChange = (
    attendanceId: string,
    justification: string,
  ) => {
    setLocalAttendance((prev) => {
      const newMap = new Map(prev);
      const current = newMap.get(attendanceId);
      newMap.set(attendanceId, {
        status: current?.status ?? "excused",
        justification,
      });
      return newMap;
    });
    setHasChanges(true);
  };

  // Miembros activos que aún NO tienen registro en esta reunión (p. ej. ingresaron
  // después de inicializar). Permite agregarlos sin duplicar ni resetear.
  const missingMembers = useMemo(() => {
    if (!members || !existingAttendance) return [];
    const have = new Set(existingAttendance.map((a) => a.memberId));
    return members.filter(
      (m: Member) => m.status === "active" && !have.has(m.id),
    );
  }, [members, existingAttendance]);

  // Filtrar por búsqueda
  const filteredAttendance = useMemo(() => {
    if (!existingAttendance) return [];
    if (!searchTerm) return existingAttendance;

    return existingAttendance.filter((a) =>
      a.personName.toLowerCase().includes(searchTerm.toLowerCase()),
    );
  }, [existingAttendance, searchTerm]);

  if (!isOpen) return null;

  const isLoading = loadingAttendance || loadingMembers;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col animate-in fade-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-blue-100 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-t-2xl">
          <div className="text-white">
            <h2 className="text-xl font-semibold">Pasar Lista</h2>
            <p className="text-blue-100 text-sm mt-1">
              {meeting.title}
              {toJsDate(meeting.date) &&
                " • " +
                  toJsDate(meeting.date)!.toLocaleDateString("es-CO", {
                    weekday: "long",
                    day: "numeric",
                    month: "long",
                  })}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/20 rounded-xl transition-colors duration-200"
          >
            <X className="w-5 h-5 text-white" />
          </button>
        </div>

        {/* Estadísticas */}
        {stats && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 sm:gap-4 p-3 sm:p-4 bg-gray-50 border-b border-gray-200">
            <div className="text-center">
              <p className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900">
                {stats.total}
              </p>
              <p className="text-xs text-gray-500">Total</p>
            </div>
            <div className="text-center">
              <p className="text-xl sm:text-2xl lg:text-3xl font-bold text-green-600">
                {stats.present}
              </p>
              <p className="text-xs text-gray-500">Presentes</p>
            </div>
            <div className="text-center">
              <p className="text-xl sm:text-2xl lg:text-3xl font-bold text-red-600">
                {stats.absent}
              </p>
              <p className="text-xs text-gray-500">Ausentes</p>
            </div>
            <div className="text-center">
              <p className="text-xl sm:text-2xl lg:text-3xl font-bold text-yellow-600">
                {stats.late}
              </p>
              <p className="text-xs text-gray-500">Tardanzas</p>
            </div>
            <div className="text-center col-span-2 sm:col-span-1">
              <p className="text-xl sm:text-2xl lg:text-3xl font-bold text-blue-600">
                {stats.attendanceRate}%
              </p>
              <p className="text-xs text-gray-500">Asistencia</p>
            </div>
          </div>
        )}

        {/* Contenido */}
        <div className="flex-1 overflow-hidden flex flex-col">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
            </div>
          ) : needsInitialization ? (
            <div className="flex-1 flex items-center justify-center p-8">
              <div className="text-center">
                <div className="w-20 h-20 bg-gradient-to-br from-blue-100 to-blue-200 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                  <Users className="w-10 h-10 text-blue-500" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Inicializar Lista de Asistencia
                </h3>
                <p className="text-gray-500 mb-6 max-w-md">
                  Se creará un registro de asistencia para cada miembro activo
                  del club. Todos iniciarán como "Ausente" y podrás cambiar el
                  estado individualmente.
                </p>
                <Button
                  onClick={handleInitializeAttendance}
                  disabled={createBulkMutation.isPending}
                >
                  {createBulkMutation.isPending ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Inicializando...
                    </>
                  ) : (
                    <>
                      <UserCheck className="w-4 h-4 mr-2" />
                      Inicializar (
                      {members?.filter((m: Member) => m.status === "active")
                        .length || 0}{" "}
                      miembros)
                    </>
                  )}
                </Button>
              </div>
            </div>
          ) : (
            <>
              {/* Barra de herramientas */}
              <div className="flex items-center justify-between gap-4 p-4 border-b border-gray-100">
                <div className="relative flex-1 max-w-xs">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Buscar miembro..."
                    className="w-full pl-10 pr-4 py-2.5 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-300"
                  />
                </div>

                <div className="flex items-center gap-2">
                  {missingMembers.length > 0 && (
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={handleInitializeAttendance}
                      disabled={createBulkMutation.isPending}
                      title="Agrega registro a los miembros que ingresaron después de inicializar"
                    >
                      <UserCheck className="w-4 h-4 mr-1" />
                      Agregar {missingMembers.length} nuevo
                      {missingMembers.length !== 1 ? "s" : ""}
                    </Button>
                  )}
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={handleMarkAllPresent}
                  >
                    <Check className="w-4 h-4 mr-1" />
                    Todos Presentes
                  </Button>
                </div>
              </div>

              {/* Lista de asistencia */}
              <div className="flex-1 overflow-y-auto p-4">
                <div className="space-y-2">
                  {filteredAttendance.map((attendance) => {
                    const localStatus =
                      localAttendance.get(attendance.id)?.status ||
                      attendance.status;

                    const localJustification =
                      localAttendance.get(attendance.id)?.justification ??
                      attendance.justification ??
                      "";
                    return (
                      <div
                        key={attendance.id}
                        className="p-3 bg-white border-2 border-gray-100 rounded-xl hover:border-gray-200 transition-colors duration-200"
                      >
                        <div className="flex items-center gap-4">
                          {/* Avatar y nombre */}
                          <div className="flex items-center gap-3 flex-1 min-w-0">
                            <div className="w-10 h-10 bg-gradient-to-br from-blue-400 to-blue-600 rounded-xl flex items-center justify-center shadow-sm shadow-blue-500/25">
                              <span className="text-white font-semibold text-sm">
                                {attendance.personName
                                  .split(" ")
                                  .map((n) => n[0])
                                  .join("")
                                  .slice(0, 2)}
                              </span>
                            </div>
                            <p className="font-medium text-gray-900 truncate">
                              {attendance.personName}
                            </p>
                          </div>

                          {/* Botones de estado */}
                          <div className="flex gap-1">
                            {statusButtons.map((btn) => (
                              <button
                                key={btn.value}
                                onClick={() =>
                                  handleStatusChange(attendance.id, btn.value)
                                }
                                className={`p-2 rounded-xl transition-colors duration-200 ${
                                  localStatus === btn.value
                                    ? btn.color +
                                      " ring-2 ring-offset-1 ring-gray-400"
                                    : "bg-gray-100 text-gray-400 hover:bg-gray-200"
                                }`}
                                title={btn.label}
                              >
                                {btn.icon}
                              </button>
                            ))}
                          </div>
                        </div>

                        {/* Motivo de justificación (solo si está "Justificado") */}
                        {localStatus === "excused" && (
                          <input
                            type="text"
                            value={localJustification}
                            onChange={(e) =>
                              handleJustificationChange(
                                attendance.id,
                                e.target.value,
                              )
                            }
                            placeholder="Motivo de la justificación (opcional)"
                            className="mt-2 w-full px-3 py-2 text-sm border border-blue-200 bg-blue-50/40 rounded-lg focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400 outline-none"
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        {!needsInitialization &&
          existingAttendance &&
          existingAttendance.length > 0 && (
            <div className="flex items-center justify-between p-4 border-t border-gray-200 bg-gray-50">
              <div className="text-sm text-gray-500">
                {hasChanges ? (
                  <span className="text-orange-600 font-medium">
                    • Hay cambios sin guardar
                  </span>
                ) : (
                  <span className="text-green-600">
                    ✓ Todos los cambios guardados
                  </span>
                )}
              </div>
              <div className="flex gap-2 sm:gap-3 flex-wrap justify-end">
                <Button variant="secondary" onClick={onClose}>
                  Salir
                </Button>
                {meeting.isClosed ? (
                  <Can permission="attendance.write" clubId={meeting.clubId}>
                    <Button
                      variant="secondary"
                      onClick={handleReopenMeeting}
                      disabled={reopenMutation.isPending}
                      title="Volver a abrir la reunión para editar la asistencia"
                    >
                      <Clock className="w-4 h-4 mr-2" />
                      Reabrir reunión
                    </Button>
                  </Can>
                ) : (
                  <Can permission="attendance.write" clubId={meeting.clubId}>
                    <Button
                      variant="secondary"
                      onClick={handleCloseMeeting}
                      disabled={
                        closeMutation.isPending || updateBulkMutation.isPending
                      }
                      className="border-green-300 text-green-700 hover:bg-green-50"
                      title="Guardar y cerrar la reunión (registra las estadísticas finales)"
                    >
                      {closeMutation.isPending ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <CheckCircle className="w-4 h-4 mr-2" />
                      )}
                      Cerrar reunión
                    </Button>
                  </Can>
                )}
                <Can permission="attendance.write" clubId={meeting.clubId}>
                  <Button
                    onClick={handleSave}
                    disabled={!hasChanges || updateBulkMutation.isPending}
                  >
                    {updateBulkMutation.isPending ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Guardando...
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4 mr-2" />
                        Guardar Cambios
                      </>
                    )}
                  </Button>
                </Can>
              </div>
            </div>
          )}
      </div>
    </div>
  );
}
